/**
Copyright (c) 2022-2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import React, { useState, useEffect } from 'react';
import {
  CpuChipIcon,
  ServerStackIcon,
  CircleStackIcon,
  ArrowPathIcon,
  SignalIcon,
  SignalSlashIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { vmApi } from '../../services/vmApi';
import { VMMetrics } from '../../types/vm';

interface VMStatsProps {
  vmId: string;
  isRunning: boolean;
  allocatedCpu: number;
  allocatedMemoryGb: number;
  allocatedStorageGb: number;
  gpuCount?: number;
  gpuType?: string;
}

const VMStats: React.FC<VMStatsProps> = ({
  vmId,
  isRunning,
  allocatedCpu,
  allocatedMemoryGb,
  allocatedStorageGb,
  gpuCount,
  gpuType,
}) => {
  const [metrics, setMetrics] = useState<VMMetrics | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);

  // Poll metrics every 5 seconds when running
  useEffect(() => {
    if (!isRunning) {
      setMetrics(null);
      return;
    }

    const fetchMetrics = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await vmApi.getVMMetrics(vmId);
        setMetrics(data);
        setLastUpdate(new Date());
      } catch (err: any) {
        setError(err.response?.data?.error || 'Failed to fetch metrics');
      } finally {
        setIsLoading(false);
      }
    };

    // Fetch immediately
    fetchMetrics();

    // Then poll every 5 seconds
    const interval = setInterval(fetchMetrics, 5000);

    return () => clearInterval(interval);
  }, [vmId, isRunning]);

  // Format bytes to human readable
  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
  };

  // Format uptime
  const formatUptime = (seconds: number): string => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (days > 0) {
      return `${days}d ${hours}h ${minutes}m`;
    } else if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  };

  // Progress bar component
  const ProgressBar = ({ value, color }: { value: number; color: string }) => (
    <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
      <div
        className={`h-full ${color} transition-all duration-500`}
        style={{ width: `${Math.min(100, Math.max(0, value))}%` }}
      />
    </div>
  );

  // Get color based on usage percentage
  const getUsageColor = (percent: number): string => {
    if (percent >= 90) return 'bg-red-500';
    if (percent >= 70) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  if (!isRunning) {
    return (
      <div className="card">
        <div className="flex items-center gap-2 mb-3">
          <CpuChipIcon className="w-5 h-5 text-gray-400" />
          <h3 className="text-base font-semibold text-white">Infrastructure Stats</h3>
        </div>
        <div className="text-center py-6 bg-gray-800 rounded-lg">
          <CpuChipIcon className="w-10 h-10 text-gray-600 mx-auto mb-2" />
          <p className="text-sm text-gray-400">VM is not running</p>
          <p className="text-xs text-gray-500">Start the VM to view stats</p>
        </div>
      </div>
    );
  }

  if (isLoading && !metrics) {
    return (
      <div className="card">
        <div className="flex items-center gap-2 mb-3">
          <CpuChipIcon className="w-5 h-5 text-gray-400" />
          <h3 className="text-base font-semibold text-white">Infrastructure Stats</h3>
        </div>
        <div className="flex items-center justify-center py-6">
          <ArrowPathIcon className="w-5 h-5 text-gray-400 animate-spin" />
          <span className="ml-2 text-sm text-gray-400">Loading...</span>
        </div>
      </div>
    );
  }

  if (error && !metrics) {
    return (
      <div className="card">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <CpuChipIcon className="w-5 h-5 text-gray-400" />
            <h3 className="text-base font-semibold text-white">Infrastructure Stats</h3>
          </div>
          <button
            onClick={() => window.location.reload()}
            className="text-xs text-primary-500 hover:text-primary-400 flex items-center gap-1"
          >
            <ArrowPathIcon className="w-3 h-3" />
            Retry
          </button>
        </div>
        <div className="text-center py-6 bg-yellow-500/20 rounded-lg border border-yellow-600">
          <SignalSlashIcon className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
          <p className="text-sm text-yellow-400">Connection failed</p>
          <p className="text-xs text-yellow-300 mt-1">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="card overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <CpuChipIcon className="w-5 h-5 text-blue-400" />
          <h3 className="text-base font-semibold text-white">Infrastructure Stats</h3>
          {metrics && (
            <span className="flex items-center gap-1 text-xs text-green-400 bg-green-500/20 px-1.5 py-0.5 rounded-full">
              <SignalIcon className="w-3 h-3" />
              Live
            </span>
          )}
        </div>
        {lastUpdate && (
          <span className="text-xs text-gray-400">
            {lastUpdate.toLocaleTimeString()}
          </span>
        )}
      </div>

      {metrics ? (
        <div className="space-y-3">
          {/* Uptime */}
          {metrics.uptime_seconds > 0 && (
            <div className="p-2 bg-gray-800 rounded-lg text-xs">
              <div className="flex items-center gap-2">
                <ClockIcon className="w-4 h-4 text-gray-400" />
                <span className="text-gray-400">Uptime: </span>
                <span className="font-medium text-gray-300">{formatUptime(metrics.uptime_seconds)}</span>
              </div>
            </div>
          )}

          {/* CPU */}
          <div className="p-2.5 bg-blue-500/20 rounded-lg">
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-1.5">
                <CpuChipIcon className="w-4 h-4 text-blue-400" />
                <span className="text-sm font-medium text-white">CPU</span>
              </div>
              <span className="text-sm font-semibold text-blue-400">
                {metrics.cpu_usage_percent.toFixed(1)}%
              </span>
            </div>
            <ProgressBar value={metrics.cpu_usage_percent} color={getUsageColor(metrics.cpu_usage_percent)} />
            <div className="mt-1 flex justify-between text-xs text-gray-400">
              <span>{metrics.cpu_usage_percent.toFixed(1)}% used</span>
              <span>{allocatedCpu} cores allocated</span>
            </div>
          </div>

          {/* Memory */}
          <div className="p-2.5 bg-purple-500/20 rounded-lg">
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-1.5">
                <ServerStackIcon className="w-4 h-4 text-purple-400" />
                <span className="text-sm font-medium text-white">Memory</span>
              </div>
              <span className="text-sm font-semibold text-purple-400">
                {metrics.memory_usage_percent.toFixed(1)}%
              </span>
            </div>
            <ProgressBar value={metrics.memory_usage_percent} color={getUsageColor(metrics.memory_usage_percent)} />
            <div className="mt-1 flex justify-between text-xs text-gray-400">
              <span>{metrics.memory_usage_gb.toFixed(2)} GB</span>
              <span>{allocatedMemoryGb} GB allocated</span>
            </div>
          </div>

          {/* Storage */}
          <div className="p-2.5 bg-orange-500/20 rounded-lg">
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-1.5">
                <CircleStackIcon className="w-4 h-4 text-orange-400" />
                <span className="text-sm font-medium text-white">Storage</span>
              </div>
              <span className="text-sm font-semibold text-orange-400">
                {metrics.disk_usage_percent.toFixed(1)}%
              </span>
            </div>
            <ProgressBar value={metrics.disk_usage_percent} color={getUsageColor(metrics.disk_usage_percent)} />
            <div className="mt-1 flex justify-between text-xs text-gray-400">
              <span>{metrics.disk_usage_gb.toFixed(2)} GB used</span>
              <span>{allocatedStorageGb || 0} GB allocated</span>
            </div>
          </div>

          {/* Network */}
          {(metrics.network_rx_bytes > 0 || metrics.network_tx_bytes > 0) && (
            <div className="p-2.5 bg-indigo-500/20 rounded-lg">
              <div className="flex items-center gap-1.5 mb-1.5">
                <SignalIcon className="w-4 h-4 text-indigo-400" />
                <span className="text-sm font-medium text-white">Network</span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <span className="text-gray-400">RX: </span>
                  <span className="font-medium text-gray-300">{formatBytes(metrics.network_rx_bytes)}</span>
                </div>
                <div>
                  <span className="text-gray-400">TX: </span>
                  <span className="font-medium text-gray-300">{formatBytes(metrics.network_tx_bytes)}</span>
                </div>
              </div>
            </div>
          )}

          {/* GPU Info (if available) */}
          {gpuCount != null && gpuCount > 0 && (
            <div className="p-2.5 bg-green-500/20 rounded-lg">
              <div className="flex items-center gap-1.5">
                <CpuChipIcon className="w-4 h-4 text-green-400" />
                <span className="text-sm font-medium text-white">GPU</span>
                {gpuType && (
                  <span className="text-xs text-gray-400">({gpuType})</span>
                )}
              </div>
              <div className="mt-1 text-xs text-gray-400">
                {gpuCount}x GPU allocated
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="flex items-center justify-center py-6">
          <ArrowPathIcon className="w-5 h-5 text-gray-400 animate-spin" />
          <span className="ml-2 text-sm text-gray-400">Loading stats...</span>
        </div>
      )}
    </div>
  );
};

export default VMStats;
